import { LightHelper } from '@/commons/lights/LightHelper';
import { ENVIRONMENT_PRESETS } from '@/commons/environment/EnvironmentPreset';
import { TweakPanel } from '@/commons/TweakPanel';
import { EnvironmentController } from '@/commons/environment/EnvironmentController';
import { Wedding3DWorld } from '@/littleWedding/Wedding3DWorld';
import { AmbientLight, SpotLight } from 'three';
import { FolderApi, SliderInputBindingApi } from 'tweakpane';
import type { IPreset } from '@/commons/renderPresetToggleList/typescript/IPreset';
import type { TSunLightPreset } from '@/commons/renderPresetToggleList/typescript/TSunLightPreset';
import type { TShadowLightPreset } from '@/commons/renderPresetToggleList/typescript/TShadowLightPreset';
import type { TAmbientLightPreset } from '@/commons/renderPresetToggleList/typescript/TAmbientLightPreset';
import type { TColor } from '@/typescript/types/TColor';

function applyRenderPreset(envController: EnvironmentController, preset: IPreset) {
    // SunLight
    applySunlightPreset(preset.sun, LightHelper.lightNames.sun);

    // ShadowLight
    applyShadowLightPreset(preset.shadow, LightHelper.lightNames.shadow);

    // AmbientLight
    applyAmbientLightPreset(preset.ambient, LightHelper.lightNames.ambient);

    // BackgroundColor
    applyBackgroundColor(envController, preset.bgColor);

    // EnvMap
    const mapValueFromEnvPreset = ENVIRONMENT_PRESETS.find(
        (envPreset: { text: string; value: string }) => envPreset.text === preset.envMap.name,
    );
    if (mapValueFromEnvPreset) {
        envController.useBackgroundAsEnvironment = preset.envMap.envAsBg;
        envController.setEnvironmentMapPreset(mapValueFromEnvPreset.value);
    }
}

export function applySunlightPreset(sunLightParams: TSunLightPreset, name: string) {
    const { color, distance, position, power, shadow } = sunLightParams;
    // SunLight
    const sunLight = Wedding3DWorld.scene?.getObjectByName(name) as SpotLight;
    if (sunLight) {
        const { x, y, z } = position;
        sunLight.position.set(x, y, z);

        const { b, g, r } = color; // as rgb value
        sunLight.color.r = r / 255;
        sunLight.color.g = g / 255;
        sunLight.color.b = b / 255;

        sunLight.power = power;
        sunLight.distance = distance;

        const { far, near } = shadow;
        sunLight.shadow.camera.near = near;
        sunLight.shadow.camera.far = far;
        sunLight.shadow.camera.updateProjectionMatrix();
        sunLight.shadow.needsUpdate = true;

        // update debug panel
        const sunLightFolder = TweakPanel.getFolderOrTabByName(name) as FolderApi;
        if (sunLightFolder?.children.length) {
            sunLightFolder.children.forEach((child) => {
                if ((child as SliderInputBindingApi)?.refresh) {
                    (child as SliderInputBindingApi)?.refresh();
                }
            });
        }
    }
}

export function applyShadowLightPreset(shadowParams: TShadowLightPreset, name: string) {
    const { color, power } = shadowParams;
    const shadowSpot = Wedding3DWorld.scene?.getObjectByName(name) as SpotLight;
    if (shadowSpot) {
        const { b, g, r } = color; // as rgb value
        shadowSpot.color.r = r / 255;
        shadowSpot.color.g = g / 255;
        shadowSpot.color.b = b / 255;

        shadowSpot.power = power;

        // update debug panel
        const shadowSpotPanel = TweakPanel.getFolderOrTabByName(name) as FolderApi;
        if (shadowSpotPanel?.children.length) {
            shadowSpotPanel.children.forEach((child) => {
                if ((child as SliderInputBindingApi)?.refresh) {
                    (child as SliderInputBindingApi)?.refresh();
                }
            });
        }
    }
}

export function applyAmbientLightPreset(ambientParams: TAmbientLightPreset, name: string) {
    const { color, power } = ambientParams;
    const ambientLight = Wedding3DWorld.scene?.getObjectByName(name) as AmbientLight;
    if (ambientLight) {
        const { b, g, r } = color;
        ambientLight.color.r = r / 255;
        ambientLight.color.g = g / 255;
        ambientLight.color.b = b / 255;
        ambientLight.intensity = power;

        // update debug panel
        const ambientLightPanel = TweakPanel.getFolderOrTabByName(name) as FolderApi;
        if (ambientLightPanel?.children.length) {
            ambientLightPanel.children.forEach((child) => {
                if ((child as SliderInputBindingApi)?.refresh) {
                    (child as SliderInputBindingApi)?.refresh();
                }
            });
        }
    }
}

export function applyBackgroundColor(envController: EnvironmentController, color: TColor) {
    const { b, g, r } = color;
    envController.setUseBackgroundAsEnvironment(false);
    envController.backgroundColor.r = r / 255;
    envController.backgroundColor.g = g / 255;
    envController.backgroundColor.b = b / 255;
    TweakPanel.getFolderOrTabByName('Environment')?.refresh();
}

export { applyRenderPreset };
